import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

interface NetworkErrorContextType {
  hasNetworkError: boolean;
  setNetworkError: (hasError: boolean) => void;
  refreshPage: () => void;
}

const NetworkErrorContext = createContext<NetworkErrorContextType | undefined>(undefined);

interface NetworkErrorProviderProps {
  children: ReactNode;
}

export const NetworkErrorProvider: React.FC<NetworkErrorProviderProps> = ({ children }) => {
  const [hasNetworkError, setHasNetworkError] = useState(false);

  const setNetworkError = useCallback((hasError: boolean) => {
    setHasNetworkError(hasError);
  }, []);

  const refreshPage = useCallback(() => {
    // First, check if we're back online
    if (navigator.onLine) {
      // Clear the error state
      setNetworkError(false);
    }
    // Reload the page
    window.location.reload();
  }, [setNetworkError]);

  const value: NetworkErrorContextType = {
    hasNetworkError,
    setNetworkError,
    refreshPage,
  };

  return <NetworkErrorContext.Provider value={value}>{children}</NetworkErrorContext.Provider>;
};

export const useNetworkError = (): NetworkErrorContextType => {
  const context = useContext(NetworkErrorContext);
  if (context === undefined) {
    throw new Error('useNetworkError must be used within a NetworkErrorProvider');
  }
  return context;
};
